/**
 * Route guard that restricts access based on authentication and role.
 *
 * Wraps page components to enforce RBAC at the routing level. Redirects
 * unauthenticated users to /login and unauthorized users to /dashboard.
 */

import { Navigate, useLocation } from 'react-router-dom';
import { rememberReturnPath } from '@/utils/afterSignIn';
import { useAuth } from '@/hooks/useAuth';
import LoadingSpinner from './LoadingSpinner';
import type { Role } from '@/types/auth';
import type { ReactNode } from 'react';

interface ProtectedRouteProps {
  children: ReactNode;
  /** Roles allowed to access this route. If empty, any authenticated user can access. */
  allowedRoles?: Role[];
}

export default function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <LoadingSpinner label="Loading..." size="lg" />;
  }

  if (!isAuthenticated) {
    // An AI app's connection request survives signing in (including SSO).
    rememberReturnPath(location.pathname + location.search);
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && allowedRoles.length > 0 && user && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}
